/**
 * WHICH ATTENDANCE CONCERNS STILL NEED SOMEBODY TO CALL, AND WHICH THE TEACHER
 * ALREADY EXPLAINED.
 *
 * Kim, relayed by Bobbie on 2026-09-28, verbatim: "Teachers asked if there is
 * a way to put a note in the rec students for when they are absent if we know
 * why. We know the comp kids and their parents can put it in the app but the
 * rec kids dont' have that so to stop Shannon from doing a call/check in when
 * the teacher knows why they were gone?"
 *
 * The server (netlify/functions/lib/attendanceConcernNotes.mjs) carries the
 * teacher's absent note beside every miss behind a concern, and sets
 * `explained` only when EVERY one of those misses has an explanation. This
 * decides what that means on screen, in one pure place shared by every
 * surface that reads useAttendanceConcerns. NOT the teacher's own Heads Up,
 * which counts from the marks alone and still lists an explained rec dancer
 * (open for Bobbie, #1293 refute round F6):
 *
 *   - A REC concern the teacher fully explained is not a call. It moves off
 *     the check-in list into its own "Explained by the teacher" group, is not
 *     offered to the check-in email, and is not counted as needing anybody.
 *     It is NOT dropped: it stays on the page, with every date and every note.
 *   - A rec concern with even one unexplained miss stays a call, and the
 *     explained dates inside it say so beside the date.
 *   - COMP concerns never move. Kim's ask was about rec families, who have no
 *     way to report an absence in the app; the comp list is about rehearsal
 *     attendance for a dance and is handled automatically. The teacher's notes
 *     still show on a comp card, beside each date.
 *
 * Import-free, so a plain node test drives it.
 */

export interface ConcernMissNote {
  date: string;
  knewInAdvance: 'yes' | 'no' | null;
  why: string;
  explained: boolean;
}

/** Only the rec rule is a check-in call. */
export const CHECK_IN_RULE = 'rec-consecutive-2';

export function splitExplainedConcerns<T extends { rule: string; explained?: boolean }>(
  concerns: readonly T[]
): { needsCall: T[]; explained: T[] } {
  const needsCall: T[] = [];
  const explained: T[] = [];
  for (const concern of concerns) {
    if (concern.rule === CHECK_IN_RULE && concern.explained === true) explained.push(concern);
    else needsCall.push(concern);
  }
  return { needsCall, explained };
}

/** The teacher's answer as one plain line beside the date. */
export function missNoteLine(note: ConcernMissNote): string {
  const parts: string[] = [];
  if (note.knewInAdvance === 'yes') parts.push('Teacher knew in advance.');
  if (note.knewInAdvance === 'no') parts.push('Teacher did not know in advance.');
  if (note.why.trim()) parts.push(`Why: ${note.why.trim()}`);
  return parts.join(' ');
}

/** The payload's notes, narrowed at the edge rather than trusted. */
export function parseMissNotes(value: unknown): ConcernMissNote[] {
  if (!Array.isArray(value)) return [];
  const out: ConcernMissNote[] = [];
  for (const raw of value) {
    if (!raw || typeof raw !== 'object') continue;
    const row = raw as Record<string, unknown>;
    if (typeof row.date !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(row.date)) continue;
    const knew = row.knewInAdvance === 'yes' || row.knewInAdvance === 'no' ? row.knewInAdvance : null;
    const why = typeof row.why === 'string' ? row.why : '';
    out.push({ date: row.date, knewInAdvance: knew, why, explained: row.explained === true });
  }
  return out;
}
